/**
 * Sprint A6.4 — Program Activation Requests history (client side).
 *
 * Shows every program activation request raised by the company with:
 *   - Request date
 *   - Current status (pending / approved / rejected)
 *   - Approval date (when decided)
 *   - Rejection reason (when applicable)
 *   - Full comment / status-history thread
 *   - Inline reply input that posts a new comment to the request
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  CheckCircle2, Clock, XCircle, MessageSquare, Loader2, Send, ArrowLeft,
} from 'lucide-react';
import { clientApi } from '@/lib/adminApi';
import { formatApiError } from '@/lib/api';
import { fmtDatePA, fmtDateTimePA } from '@/lib/panamaTime';
import i18next from 'i18next';
import { toast } from 'sonner';

const i18nLang = () => i18next.language || 'es';

// Client-facing program display name — raw catalog keys must never leak.
const programName = (t, code) =>
  t(`clientTalentHeart.programsReco.items.${code}.name`, { defaultValue: code });

const STATUS_META = {
  pending:          { icon: Clock,         className: 'bg-warning/15 text-warning border-warning/30',              key: 'statusPending' },
  info_required:    { icon: MessageSquare, className: 'bg-info/15 text-info border-info/30',                       key: 'statusInfoRequired' },
  approved:         { icon: CheckCircle2,  className: 'bg-success/15 text-success border-success/30',              key: 'statusApproved' },
  rejected:         { icon: XCircle,       className: 'bg-destructive/15 text-destructive border-destructive/30',  key: 'statusRejected' },
  closed_duplicate: { icon: CheckCircle2,  className: 'bg-muted text-muted-foreground border-border',              key: 'statusClosedDuplicate' },
  closed_completed: { icon: CheckCircle2,  className: 'bg-muted text-muted-foreground border-border',              key: 'statusClosedCompleted' },
  closed:           { icon: XCircle,       className: 'bg-muted text-muted-foreground border-border',              key: 'statusClosed' },
};

function StatusBadge({ status }) {
  const { t } = useTranslation();
  const meta = STATUS_META[status] || STATUS_META.pending;
  const Icon = meta.icon;
  return (
    <Badge className={`gap-1 ${meta.className}`} data-testid={`par-status-${status}`}>
      <Icon className="h-3 w-3" />
      {t(`programActivationHistory.${meta.key}`)}
    </Badge>
  );
}

function HistoryEntry({ entry, t }) {
  const Icon = entry.action === 'approved'
    ? CheckCircle2
    : entry.action === 'rejected' ? XCircle
      : entry.action === 'comment' ? MessageSquare : Clock;
  const colorClass = entry.action === 'approved'
    ? 'text-success'
    : entry.action === 'rejected' ? 'text-destructive'
      : entry.action === 'comment' ? 'text-primary' : 'text-muted-foreground';
  const date = fmtDateTimePA(entry.at, i18nLang());
  return (
    <li className="flex gap-3 py-2 border-b border-border/50 last:border-b-0"
        data-testid={`par-history-${entry.id}`}>
      <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${colorClass}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-sm font-medium">
            {entry.by_user_name || '—'} <span className="text-muted-foreground font-normal">· {entry.by_user_role || ''}</span>
          </span>
          <span className="text-xs text-muted-foreground tabular-nums">{date}</span>
        </div>
        <p className="text-sm text-muted-foreground mt-0.5">
          <span className="font-medium text-foreground">
            {t(`programActivationHistory.action.${entry.action}`)}
          </span>
          {entry.reason && (
            <span data-testid={`par-reason-${entry.id}`}> · {t('programActivationHistory.reasonLabel')}: <em>{entry.reason}</em></span>
          )}
          {entry.comment && (
            <span data-testid={`par-comment-${entry.id}`}>: {entry.comment}</span>
          )}
        </p>
      </div>
    </li>
  );
}

function RequestDetail({ req, onBack, onUpdate, t }) {
  const [body, setBody] = useState('');
  const [posting, setPosting] = useState(false);

  const send = useCallback(async () => {
    const text = body.trim();
    if (!text) return;
    setPosting(true);
    try {
      await clientApi.postProgramActivationMessage(req.id, { body: text });
      setBody('');
      const fresh = await clientApi.getProgramActivationRequest(req.id);
      onUpdate(fresh);
      toast.success(t('programActivationHistory.commentSent'));
    } catch (e) {
      toast.error(t('programActivationHistory.commentFailed'), { description: formatApiError(e) });
    } finally {
      setPosting(false);
    }
  }, [body, req.id, onUpdate, t]);

  const requested = fmtDateTimePA(req.created_at, i18nLang());
  const decided = req.decided_at ? fmtDateTimePA(req.decided_at, i18nLang()) : null;

  return (
    <div className="space-y-3" data-testid={`par-detail-${req.id}`}>
      <Button size="sm" variant="ghost" onClick={onBack} data-testid="par-back" className="gap-1">
        <ArrowLeft className="h-3 w-3" /> {t('programActivationHistory.back')}
      </Button>

      <Card className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">
              {t('programActivationHistory.programLabel')}
            </p>
            <p className="font-semibold mt-0.5" data-testid="par-detail-program">{programName(t, req.program_code)}</p>
          </div>
          <StatusBadge status={req.status} />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3 text-sm">
          <div>
            <p className="text-xs text-muted-foreground">{t('programActivationHistory.requestedAt')}</p>
            <p className="font-medium" data-testid="par-detail-requested">{requested}</p>
          </div>
          {decided && (
            <div>
              <p className="text-xs text-muted-foreground">
                {req.status === 'approved'
                  ? t('programActivationHistory.approvedAt')
                  : t('programActivationHistory.rejectedAt')}
              </p>
              <p className="font-medium" data-testid="par-detail-decided">{decided}</p>
            </div>
          )}
        </div>

        {req.status === 'approved' && (
          <div className="mt-3 p-3 rounded-md bg-success/10 border border-success/30"
               data-testid="par-detail-approved-next">
            <p className="text-sm">{t('programActivationHistory.approvedNextStep')}</p>
          </div>
        )}

        {['closed_duplicate', 'closed_completed', 'closed'].includes(req.status) && req.close_reason && (
          <div className="mt-3 p-3 rounded-md bg-muted/60 border" data-testid="par-detail-close-reason">
            <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
              {t('programActivationHistory.closeReasonLabel')}
            </p>
            <p className="text-sm mt-1">{req.close_reason}</p>
          </div>
        )}

        {req.status === 'rejected' && req.reject_reason && (
          <div className="mt-3 p-3 rounded-md bg-destructive/10 border border-destructive/30"
               data-testid="par-detail-reject-reason">
            <p className="text-xs uppercase tracking-wider text-destructive font-medium">
              {t('programActivationHistory.rejectReasonLabel')}
            </p>
            <p className="text-sm mt-1">{req.reject_reason}</p>
          </div>
        )}
      </Card>

      <Card className="p-4">
        <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
          {t('programActivationHistory.historyLabel')}
        </p>
        <ul className="divide-y divide-border/50" data-testid="par-detail-history">
          {(req.status_history || []).map((h) => (
            <HistoryEntry key={h.id} entry={h} t={t} />
          ))}
        </ul>
      </Card>

      <Card className="p-4">
        <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
          {t('programActivationHistory.replyLabel')}
        </p>
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={t('programActivationHistory.replyPlaceholder')}
          rows={3}
          data-testid="par-reply-input"
          maxLength={2000}
        />
        <div className="flex justify-end mt-2">
          <Button size="sm" onClick={send} disabled={posting || !body.trim()}
                  data-testid="par-reply-submit" className="gap-1.5">
            {posting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
            {t('programActivationHistory.send')}
          </Button>
        </div>
      </Card>
    </div>
  );
}

export default function ProgramActivationHistoryDialog({ open, onOpenChange }) {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await clientApi.listProgramActivationRequests();
      setItems(data?.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setSelected(null);
      load();
    }
  }, [open, load]);

  const onUpdate = useCallback((fresh) => {
    setSelected(fresh);
    setItems((prev) => prev.map((it) => (it.id === fresh.id ? fresh : it)));
  }, []);

  const summary = useMemo(() => ({
    pending:  items.filter((i) => ['pending', 'info_required'].includes(i.status)).length,
    approved: items.filter((i) => i.status === 'approved').length,
    rejected: items.filter((i) => ['rejected', 'closed_duplicate', 'closed_completed', 'closed'].includes(i.status)).length,
  }), [items]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="par-history-dialog">
        <DialogHeader>
          <DialogTitle>{t('programActivationHistory.title')}</DialogTitle>
          <DialogDescription>{t('programActivationHistory.subtitle')}</DialogDescription>
        </DialogHeader>

        {selected ? (
          <RequestDetail req={selected} onBack={() => setSelected(null)} onUpdate={onUpdate} t={t} />
        ) : (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm items-center" data-testid="par-summary">
              <span className="inline-flex items-center gap-1.5">
                <span className="text-xs text-muted-foreground">{t('programActivationHistory.summaryOpen')}</span>
                <span className="tabular-nums font-semibold" data-testid="par-summary-open">{summary.pending}</span>
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="text-xs text-muted-foreground">{t('programActivationHistory.summaryApproved')}</span>
                <span className="tabular-nums font-semibold" data-testid="par-summary-approved">{summary.approved}</span>
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="text-xs text-muted-foreground">{t('programActivationHistory.summaryClosed')}</span>
                <span className="tabular-nums font-semibold" data-testid="par-summary-closed">{summary.rejected}</span>
              </span>
            </div>

            {loading ? (
              <div className="py-10 flex items-center justify-center text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin mr-2" /> {t('common.loading')}
              </div>
            ) : items.length === 0 ? (
              <p className="py-10 text-center text-sm text-muted-foreground" data-testid="par-empty">
                {t('programActivationHistory.empty')}
              </p>
            ) : (
              <ul className="space-y-2" data-testid="par-history-list">
                {items.map((it) => {
                  const date = fmtDatePA(it.created_at, i18nLang());
                  const comments = (it.conversation || []).length;
                  return (
                    <li key={it.id}>
                      <button
                        type="button"
                        onClick={() => setSelected(it)}
                        className="w-full text-left p-3 rounded-lg border border-border/60 hover:border-primary/50 hover:bg-muted/30 transition-smooth"
                        data-testid={`par-row-${it.id}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="font-medium text-sm truncate">{programName(t, it.program_code)}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {t('programActivationHistory.requestedOn', { date })}
                              {comments > 0 && ` · ${t('programActivationHistory.commentsCount', { count: comments })}`}
                            </p>
                          </div>
                          <StatusBadge status={it.status} />
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
